<app-page-layout 
  [title]="title"
  [subtitle]="subtitle"
  [phoneNumber]="phoneNumber"
  [heroImage]="heroImage"
  [breadcrumbs]="breadcrumbs"
  [prevPage]="prevPage"
  [nextPage]="nextPage">
  
  <div class="content-section">
    <h2>L'assurance dommages-ouvrage : protection du maître d'ouvrage</h2>
    <p>
      L'<strong>assurance dommages-ouvrage (DO)</strong> est une assurance <strong>obligatoire</strong> 
      pour tout maître d'ouvrage (particulier ou professionnel) qui fait construire ou réaliser des travaux importants. 
      Elle garantit le <strong>préfinancement</strong> des réparations des dommages relevant de la garantie décennale, 
      <strong>sans attendre</strong> qu'une décision de justice désigne le responsable.
    </p>
    <p>
      C'est une assurance <strong>sans recours</strong> : elle indemnise rapidement le maître d'ouvrage, 
      puis se retourne contre les constructeurs responsables. Elle permet d'éviter les longs procès 
      et de réparer rapidement les dommages.
    </p>
  </div>

  <div class="content-section bg-warning">
    <div class="alert-obligation">
      <i class="pi pi-exclamation-triangle"></i>
      <div>
        <h3>⚠️ Obligation légale pour le maître d'ouvrage</h3>
        <p>
          Depuis la loi Spinetta de 1978, l'assurance dommages-ouvrage est <strong>obligatoire</strong> 
          pour tout maître d'ouvrage (particulier, promoteur, entreprise, collectivité) qui fait construire :
        </p>
        <ul>
          <li>Construction de maison individuelle</li>
          <li>Construction d'immeuble</li>
          <li>Travaux importants (extension, surélévation, rénovation lourde)</li>
          <li>Travaux touchant au gros œuvre ou à l'étanchéité</li>
        </ul>
        <p class="sanction"><strong>Sanction en cas de non-souscription :</strong> Amende de 75 000€ et difficultés de revente (absence d'attestation DO).</p>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Qui doit souscrire une assurance dommages-ouvrage ?</h2>
    <div class="subscribers-list">
      <div class="subscriber-card">
        <i class="pi pi-user"></i>
        <h3>Particuliers</h3>
        <p>Faisant construire leur maison individuelle ou réalisant des travaux d'extension/rénovation lourde.</p>
        <p class="example">Exemple : Construction maison 150m²</p>
      </div>

      <div class="subscriber-card">
        <i class="pi pi-building"></i>
        <h3>Promoteurs immobiliers</h3>
        <p>Construction d'immeubles collectifs (appartements, bureaux) destinés à la vente ou à la location.</p>
        <p class="example">Exemple : Résidence 20 logements</p>
      </div>

      <div class="subscriber-card">
        <i class="pi pi-briefcase"></i>
        <h3>Entreprises</h3>
        <p>Faisant construire ou rénover leurs locaux professionnels (bureaux, entrepôts, usines).</p>
        <p class="example">Exemple : Construction entrepôt 2000m²</p>
      </div>

      <div class="subscriber-card">
        <i class="pi pi-globe"></i>
        <h3>Collectivités locales</h3>
        <p>Construction d'équipements publics (écoles, mairies, gymnases, crèches).</p>
        <p class="example">Exemple : Construction école primaire</p>
      </div>

      <div class="subscriber-card">
        <i class="pi pi-home"></i>
        <h3>Bailleurs sociaux</h3>
        <p>Construction de logements sociaux pour location longue durée.</p>
        <p class="example">Exemple : Résidence HLM 50 logements</p>
      </div>
    </div>
  </div>

  <div class="content-section bg-light">
    <h2>Tarification selon le type de projet</h2>
    <div class="pricing-projects">
      <div class="project-pricing">
        <h3>🏠 Maison individuelle</h3>
        <div class="project-details">
          <p><strong>Base de calcul :</strong> 2% à 4% du coût total TTC des travaux</p>
          <div class="examples">
            <div class="example-line">
              <span>Maison 100m² (200 000€ TTC)</span>
              <span class="price">4 000€ - 8 000€</span>
            </div>
            <div class="example-line">
              <span>Maison 150m² (300 000€ TTC)</span>
              <span class="price">6 000€ - 12 000€</span>
            </div>
            <div class="example-line">
              <span>Maison 200m² (400 000€ TTC)</span>
              <span class="price">8 000€ - 16 000€</span>
            </div>
          </div>
        </div>
      </div>

      <div class="project-pricing">
        <h3>🏢 Immeuble collectif</h3>
        <div class="project-details">
          <p><strong>Base de calcul :</strong> 1,5% à 3% du coût total TTC (dégressif avec taille)</p>
          <div class="examples">
            <div class="example-line">
              <span>Immeuble 10 logements (2M€ TTC)</span>
              <span class="price">30 000€ - 60 000€</span>
            </div>
            <div class="example-line">
              <span>Immeuble 20 logements (4M€ TTC)</span>
              <span class="price">60 000€ - 120 000€</span>
            </div>
            <div class="example-line">
              <span>Immeuble 50 logements (10M€ TTC)</span>
              <span class="price">150 000€ - 300 000€</span>
            </div>
          </div>
        </div>
      </div>

      <div class="project-pricing">
        <h3>🏗️ Locaux professionnels</h3>
        <div class="project-details">
          <p><strong>Base de calcul :</strong> 2% à 3,5% du coût total TTC</p>
          <div class="examples">
            <div class="example-line">
              <span>Bureaux 500m² (800 000€ TTC)</span>
              <span class="price">16 000€ - 28 000€</span>
            </div>
            <div class="example-line">
              <span>Entrepôt 2000m² (1,5M€ TTC)</span>
              <span class="price">30 000€ - 52 500€</span>
            </div>
            <div class="example-line">
              <span>Usine 5000m² (5M€ TTC)</span>
              <span class="price">100 000€ - 175 000€</span>
            </div>
          </div>
        </div>
      </div>

      <div class="project-pricing">
        <h3>🔧 Travaux de rénovation lourde</h3>
        <div class="project-details">
          <p><strong>Base de calcul :</strong> 2,5% à 5% du coût total TTC (risque plus élevé)</p>
          <div class="examples">
            <div class="example-line">
              <span>Extension 50m² (100 000€ TTC)</span>
              <span class="price">2 500€ - 5 000€</span>
            </div>
            <div class="example-line">
              <span>Surélévation (150 000€ TTC)</span>
              <span class="price">3 750€ - 7 500€</span>
            </div>
            <div class="example-line">
              <span>Rénovation totale (250 000€ TTC)</span>
              <span class="price">6 250€ - 12 500€</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Critères d'évaluation de la prime</h2>
    <div class="evaluation-criteria">
      <div class="criterion-group">
        <h3>🏗️ Complexité du projet</h3>
        <ul>
          <li><strong>Construction simple (maison plain-pied) :</strong> Tarif de base (2% à 3%)</li>
          <li><strong>Architecture complexe (étages, sous-sol, toiture complexe) :</strong> +0,5% à +1%</li>
          <li><strong>Techniques spéciales (ossature bois, béton cellulaire) :</strong> +0,5% à +1,5%</li>
          <li><strong>Équipements techniques avancés (domotique, géothermie) :</strong> +0,3% à +0,8%</li>
        </ul>
      </div>

      <div class="criterion-group">
        <h3>📍 Localisation du chantier</h3>
        <ul>
          <li><strong>Zone non sismique, climat tempéré :</strong> Tarif normal</li>
          <li><strong>Zone sismique (Antilles, Alpes) :</strong> Surprime +0,5% à +1%</li>
          <li><strong>Zone inondable :</strong> Surprime +0,3% à +0,8%</li>
          <li><strong>Zone littorale (corrosion saline) :</strong> Surprime +0,2% à +0,5%</li>
          <li><strong>Terrain difficile (pente, remblai) :</strong> Surprime +0,5% à +1,2%</li>
        </ul>
      </div>

      <div class="criterion-group">
        <h3>👷 Qualité des intervenants</h3>
        <ul>
          <li><strong>Constructeurs qualifiés (Qualibat, RGE) :</strong> Réduction -0,3% à -0,5%</li>
          <li><strong>Architecte DPLG inscrit à l'ordre :</strong> Réduction -0,2% à -0,4%</li>
          <li><strong>Bureau de contrôle obligatoire :</strong> Réduction -0,3% à -0,6%</li>
          <li><strong>Auto-constructeurs ou artisans non assurés :</strong> Surprime +1% à +2% (voire refus)</li>
        </ul>
      </div>

      <div class="criterion-group">
        <h3>🕐 Nature des travaux</h3>
        <ul>
          <li><strong>Construction neuve sur terrain vierge :</strong> Tarif de base</li>
          <li><strong>Extension/surélévation :</strong> Surprime +0,5% à +1,5% (risque reprise anciens ouvrages)</li>
          <li><strong>Rénovation lourde :</strong> Surprime +1% à +2% (état existant inconnu)</li>
          <li><strong>Travaux sur bâtiment classé/ancien :</strong> Surprime +1,5% à +3%</li>
        </ul>
      </div>
    </div>
  </div>

  <div class="content-section bg-accent">
    <h2>Que couvre l'assurance dommages-ouvrage ?</h2>
    <div class="coverage-do">
      <div class="covered-damages">
        <h3>✅ Dommages couverts (garantie décennale)</h3>
        <p>L'assurance DO couvre les mêmes dommages que la garantie décennale :</p>
        <ul>
          <li><strong>Atteintes à la solidité de l'ouvrage :</strong>
            <ul>
              <li>Fissures importantes menaçant la structure</li>
              <li>Affaissement ou tassement des fondations</li>
              <li>Effondrement partiel ou total</li>
            </ul>
          </li>
          <li><strong>Dommages rendant l'ouvrage impropre à destination :</strong>
            <ul>
              <li>Infiltrations d'eau rendant les locaux inhabitables</li>
              <li>Défaut d'isolation thermique majeur</li>
              <li>Installation de chauffage défectueuse</li>
              <li>Étanchéité défaillante (toiture, terrasse)</li>
            </ul>
          </li>
          <li><strong>Éléments d'équipement indissociables :</strong>
            <ul>
              <li>Menuiseries extérieures (portes, fenêtres)</li>
              <li>Installations sanitaires intégrées</li>
              <li>Système électrique encastré</li>
            </ul>
          </li>
        </ul>
      </div>

      <div class="exclusions">
        <h3>❌ Exclusions</h3>
        <ul>
          <li><strong>Dommages esthétiques :</strong> défauts de peinture, fissures superficielles</li>
          <li><strong>Dommages apparents à réception :</strong> couverts par garantie de parfait achèvement (1 an)</li>
          <li><strong>Désordres de bon fonctionnement :</strong> couverts par garantie biennale (2 ans)</li>
          <li><strong>Usure normale et défaut d'entretien</strong></li>
          <li><strong>Catastrophes naturelles :</strong> couvertes par assurance multirisque habitation</li>
        </ul>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Fonctionnement et mise en œuvre</h2>
    <div class="how-it-works">
      <div class="step">
        <div class="step-number">1</div>
        <div class="step-content">
          <h4>Souscription avant ouverture du chantier</h4>
          <p>Le maître d'ouvrage souscrit l'assurance DO <strong>avant le début des travaux</strong>. 
          L'assureur réalise une étude du projet (plans, devis, intervenants).</p>
          <p class="timeline">⏱️ Délai : 2 à 4 semaines</p>
        </div>
      </div>

      <div class="step">
        <div class="step-number">2</div>
        <div class="step-content">
          <h4>Pendant les travaux</h4>
          <p>Le contrat est en attente. L'assurance DO ne couvre pas les malfaçons pendant le chantier 
          (c'est la responsabilité du constructeur et de son assurance décennale).</p>
        </div>
      </div>

      <div class="step">
        <div class="step-number">3</div>
        <div class="step-content">
          <h4>Réception des travaux</h4>
          <p>Le maître d'ouvrage réceptionne les travaux. <strong>C'est le point de départ de la garantie DO</strong> 
          (10 ans comme la décennale).</p>
          <p class="timeline">📅 Début de couverture</p>
        </div>
      </div>

      <div class="step">
        <div class="step-number">4</div>
        <div class="step-content">
          <h4>Apparition d'un désordre (pendant 10 ans)</h4>
          <p>Si un dommage relevant de la garantie décennale apparaît, le maître d'ouvrage déclare le sinistre 
          à son assurance DO (pas besoin d'attendre une décision de justice).</p>
        </div>
      </div>

      <div class="step">
        <div class="step-number">5</div>
        <div class="step-content">
          <h4>Expertise et indemnisation rapide</h4>
          <p>L'assureur DO mandate un expert, évalue les dommages et <strong>préfinance les réparations sous 90 jours</strong> 
          (délai légal). Ensuite, l'assureur se retourne contre les constructeurs responsables et leurs assurances décennales.</p>
          <p class="advantage">✅ Avantage : Réparations rapides sans attendre des années de procès</p>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section bg-light">
    <h2>Exemple de calcul détaillé</h2>
    <div class="detailed-calculation">
      <div class="calc-header">
        <h3>🏠 Construction maison individuelle - 150m²</h3>
        <p><strong>Coût total des travaux :</strong> 300 000€ TTC | <strong>Localisation :</strong> Région parisienne | <strong>Constructeur qualifié</strong></p>
      </div>
      <div class="calc-body">
        <div class="calc-line">
          <span>Prime de base (3% du coût TTC)</span>
          <span>9 000€</span>
        </div>
        <div class="calc-line">
          <span>+ Architecture avec étage et sous-sol (+0,5%)</span>
          <span>+1 500€</span>
        </div>
        <div class="calc-line">
          <span>+ Équipements spéciaux (pompe à chaleur, VMC double flux) (+0,3%)</span>
          <span>+900€</span>
        </div>
        <div class="calc-line reduction">
          <span>- Constructeur qualifié Qualibat (-0,4%)</span>
          <span>-1 200€</span>
        </div>
        <div class="calc-line reduction">
          <span>- Bureau de contrôle (-0,4%)</span>
          <span>-1 200€</span>
        </div>
        <div class="calc-line total">
          <span><strong>Prime totale assurance DO</strong></span>
          <span class="final-price">9 000€</span>
        </div>
        <div class="calc-note">
          <p><strong>Paiement :</strong> Prime unique payée au début des travaux (pas de mensualités)</p>
          <p><strong>Couverture :</strong> 10 ans à partir de la réception des travaux</p>
        </div>
      </div>
    </div>
  </div>

  <div class="content-section">
    <h2>Différence entre assurance décennale et dommages-ouvrage</h2>
    <div class="comparison-table-wrapper">
      <table class="comparison-table">
        <thead>
          <tr>
            <th>Critère</th>
            <th>Assurance Décennale</th>
            <th>Assurance Dommages-Ouvrage</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Qui doit souscrire ?</strong></td>
            <td>Le constructeur (artisan, entreprise, architecte)</td>
            <td>Le maître d'ouvrage (particulier, promoteur, entreprise)</td>
          </tr>
          <tr>
            <td><strong>Objectif</strong></td>
            <td>Couvrir la responsabilité du constructeur</td>
            <td>Garantir le préfinancement rapide des réparations</td>
          </tr>
          <tr>
            <td><strong>Déclenchement</strong></td>
            <td>Après désignation du responsable (souvent long procès)</td>
            <td>Immédiat dès constatation du sinistre (sans attendre procès)</td>
          </tr>
          <tr>
            <td><strong>Délai d'indemnisation</strong></td>
            <td>Plusieurs mois/années (après décision de justice)</td>
            <td>90 jours maximum (délai légal)</td>
          </tr>
          <tr>
            <td><strong>Recours</strong></td>
            <td>Le maître d'ouvrage se retourne contre le constructeur</td>
            <td>L'assureur DO se retourne contre les constructeurs après avoir indemnisé</td>
          </tr>
          <tr>
            <td><strong>Durée</strong></td>
            <td>10 ans à partir de la réception</td>
            <td>10 ans à partir de la réception</td>
          </tr>
          <tr>
            <td><strong>Coût</strong></td>
            <td>500€ à 15 000€/an selon activité et CA</td>
            <td>2% à 4% du coût total TTC (prime unique)</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div class="content-section">
    <h2>Conseils pour réduire le coût de votre assurance DO</h2>
    <div class="cost-tips">
      <div class="tip-card">
        <i class="pi pi-check-circle"></i>
        <h4>Choisissez des constructeurs qualifiés</h4>
        <p>Les assureurs accordent des réductions si les intervenants ont des qualifications (Qualibat, RGE). 
        Exigez les attestations d'assurance décennale de tous les constructeurs.</p>
      </div>

      <div class="tip-card">
        <i class="pi pi-eye"></i>
        <h4>Faites appel à un bureau de contrôle</h4>
        <p>Même si non obligatoire pour les maisons individuelles, un bureau de contrôle rassure l'assureur 
        et peut réduire la prime de 0,3% à 0,6%.</p>
      </div>

      <div class="tip-card">
        <i class="pi pi-file"></i>
        <h4>Préparez un dossier complet</h4>
        <p>Plans détaillés, devis précis, études de sol, permis de construire. Un dossier clair facilite 
        l'étude de l'assureur et évite les surprimes pour risque mal évalué.</p>
      </div>

      <div class="tip-card">
        <i class="pi pi-chart-bar"></i>
        <h4>Optez pour une conception simple</h4>
        <p>Les architectures complexes (formes irrégulières, toitures compliquées) augmentent la prime. 
        Privilégiez la simplicité si le budget est serré.</p>
      </div>

      <div class="tip-card">
        <i class="pi pi-calendar"></i>
        <h4>Comparez les offres</h4>
        <p>Les tarifs varient fortement d'un assureur à l'autre (jusqu'à 30% d'écart). 
        Faites jouer la concurrence, surtout pour les gros projets.</p>
      </div>

      <div class="tip-card">
        <i class="pi pi-shield"></i>
        <h4>Négociez selon la taille du projet</h4>
        <p>Pour les promoteurs ou entreprises réalisant plusieurs opérations par an, 
        négociez un contrat-cadre avec des tarifs dégressifs.</p>
      </div>
    </div>
  </div>

  <div class="content-section cta-section">
    <h2>Trouvez votre assurance dommages-ouvrage au meilleur prix</h2>
    <p>Comparez les offres pour votre projet de construction</p>
    <button class="btn-primary" routerLink="/cac/comparaison">Comparer les offres</button>
  </div>
</app-page-layout>
